import axios from "axios";
import { apiEndpoint, apiEndpointFunction } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';
import qs from 'qs';

export const sprayService = {
  getList,
  getDetail,
  storeOrUpdate,
  destroy,
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  dashboard,
  markAsSprayCleanup,
  markAsWaitingForCompletion,
  completeJob,
  downloadReportPdf,
  getSettings,
  updateSetting,
  getChangeLog,
  startSpraying,
  stopSpraying,
  pauseUnplannedRefill,
  refillSpray,
  startZone,
  finishZone,
  logRefill,
  logInitialFill,
  logPpeCheck,
  logTask,
};

function getList(propertyId, params = {}) {
  const queryString = qs.stringify({property_id: propertyId, ...params}, { addQueryPrefix: true });
  return (
    axios
      .get(
        `${apiEndpoint.sprayJobs}${queryString}`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if (res && res.status === 200 && res.data.code === '0000') {
          return res.data;
        }
      })
  );
}

function getDetail(id) {
  return (
    axios
      .get(
        apiEndpointFunction.sprayJobs(id) + '?expand=chemicals.asset,latest_session,sprayer.spray',
        {
          headers: authHeader(),
        }
      ).then(res => {
        if (res && res.status === 200 && res.data.code === '0000') {
          return res.data;
        }
      })
  );
}

function storeOrUpdate(data, id = null) {
  return (id ? (
    axios
      .put(
        apiEndpointFunction.sprayJobs(id),
        data,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if (res && res.status === 200 && res.data.code === '0000') {
          return res.data;
        }
      })
  ) : (
    axios
      .post(
        apiEndpoint.sprayJobs,
        data,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if (res && res.status === 200 && res.data.code === '0000') {
          return res.data;
        }
      })
  ));
}

function destroy(id) {
  return (
    axios
      .delete(
        apiEndpointFunction.sprayJobs(id),
        {
          headers: authHeader(),
        }
      ).then(res => {
        if (res && res.status === 200 && res.data.code === '0000') {
          return res.data;
        }
      })
  );
}

function getTemplates(propertyId) {
  return (
    axios
      .get(
        `${apiEndpoint.sprayJobTemplates}?property_id=${propertyId}`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if (res && res.status === 200 && res.data.code === '0000') {
          return res.data;
        }
      })
  );
}

function createTemplate(data) {
  return (
    axios
      .post(
        `${apiEndpoint.sprayJobTemplates}`,
        data,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if (res && res.status === 200 && res.data.code === '0000') {
          return res.data;
        }
      })
  );
}

function updateTemplate(id, data) {
  return (
    axios
      .put(
        `${apiEndpoint.sprayJobTemplates}/${id}`,
        data,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if (res && res.status === 200 && res.data.code === '0000') {
          return res.data;
        }
      })
  );
}

function deleteTemplate(id) {
  return (
    axios
      .delete(
        `${apiEndpoint.sprayJobTemplates}/${id}`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if (res && res.status === 200 && res.data.code === '0000') {
          return res.data;
        }
      })
  );
}

function dashboard(propertyId) {
  return (
    axios
      .get(
        `${apiEndpoint.sprayJobs}/dashboard?property_id=${propertyId}&expand=chemicals.asset,sprayer.spray,latest_session`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if (res && res.status === 200 && res.data.code === '0000') {
          return res.data;
        }
      })
  );
}

/**
 * Move a spray job into the spray_cleanup phase (post-spraying, pre-completion).
 * Introduced as part of Damien's spray UX redesign.
 * @param {number} jobId
 * @returns {Promise<Object>}
 */
function markAsSprayCleanup(jobId) {
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/mark-spray-cleanup`,
      {},
      { headers: authHeader() }
    )
    .then((res) => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
      throw new Error(res?.data?.message || 'Failed to move job to cleanup');
    });
}

/**
 * Download the spray job report PDF and trigger a browser save. We can't use
 * window.open because the API requires a Bearer token in the Authorization
 * header — query-string auth would otherwise leak the JWT into browser
 * history and HTTP logs. Instead we fetch as a blob and synthesise an <a>
 * click.
 *
 * @param {number} jobId
 * @returns {Promise<void>}
 */
function downloadReportPdf(jobId) {
  return axios
    .get(`${apiEndpoint.sprayJobs}/${jobId}/report`, {
      headers: { ...authHeader(), Accept: 'application/pdf' },
      responseType: 'blob',
    })
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `spray-job-${jobId}-report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    });
}

/**
 * Mark a spray job as waiting for completion
 * @param {number} jobId - The ID of the spray job
 * @returns {Promise<Object>} The API response
 */
function markAsWaitingForCompletion(jobId) {
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/mark-waiting-for-completion`,
      {},
      {
        headers: authHeader(),
      }
    )
    .then((res) => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
      throw new Error(res?.data?.message || 'Failed to mark job as waiting for completion');
    });
}

/**
 * Complete a spray job
 * @param {number} jobId - The ID of the spray job
 * @returns {Promise<Object>} The API response
 */
function completeJob(jobId) {
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/complete`,
      {},
      { headers: authHeader() }
    )
    .then((res) => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
      throw new Error(res?.data?.message || 'Failed to complete job');
    });
}

function getSettings(propertyId) {
  return axios
    .get(
      `${apiEndpoint.spraySettings}?property_id=${propertyId}`,
      { headers: authHeader() }
    )
    .then((res) => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
      return null;
    });
}

function updateSetting(data) {
  return axios
    .put(
      apiEndpoint.spraySettings,
      data,
      { headers: authHeader() }
    )
    .then((res) => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
      throw new Error(res?.data?.message || 'Failed to save setting');
    });
}

function getChangeLog(jobId) {
  return axios
    .get(
      `${apiEndpoint.sprayJobs}/${jobId}/change-log`,
      { headers: authHeader() }
    )
    .then((res) => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
      return null;
    });
}

function startSpraying(jobId) {
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/start-spraying`,
      {},
      { headers: authHeader() }
    )
    .then((res) => {
      if (res && res.status === 200) {
        return res.data;
      }
      return { code: 'error', message: 'Unexpected response' };
    });
}

function stopSpraying(jobId, pauseZone = null) {
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/stop-spraying`,
      pauseZone ? { pause_zone: pauseZone } : {},
      { headers: authHeader() }
    )
    .then((res) => {
      if (res && res.status === 200) {
        return res.data;
      }
      return { code: 'error', message: 'Unexpected response' };
    });
}

/**
 * Pause spraying AND inject an unplanned refill marker after the
 * active zone. Used when the operator runs out of mix mid-zone and
 * needs to top up before resuming — the existing planned-refill
 * workflow then gates the resume.
 *
 * @param {number} jobId
 * @param {Object} params
 * @param {Object} params.pauseZone     — { letter, bench, row, notes? }
 * @param {number} params.liters        — total volume to load (required, > 0)
 * @returns {Promise<Object>}
 */
function pauseUnplannedRefill(jobId, { pauseZone, liters }) {
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/pause-unplanned-refill`,
      {
        pause_zone: pauseZone || null,
        liters,
      },
      { headers: authHeader() }
    )
    .then((res) => {
      if (res && res.status === 200) {
        return res.data;
      }
      return { code: 'error', message: 'Unexpected response' };
    });
}

// ---------------------------------------------------------------------------
// Unified single-page workflow (round-4): per-zone Start / Finish + refill
// log without cloning. The index parameter is the position of the item in
// the heterogeneous zones JSON list (zones interleaved with refill markers).
// ---------------------------------------------------------------------------

function startZone(jobId, index, body = {}) {
  // Body can carry { delta_t } — the live Delta T reading at the
  // moment Start was pressed. Backend stores it in the audit-log
  // metadata so the Action Log shows which weather the operator
  // committed to (round-6).
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/start-zone/${index}`,
      body || {},
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200)
      ? res.data
      : { code: 'error', message: 'Unexpected response' });
}

function finishZone(jobId, index) {
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/finish-zone/${index}`,
      {},
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200)
      ? res.data
      : { code: 'error', message: 'Unexpected response' });
}

function logRefill(jobId, index) {
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/log-refill/${index}`,
      {},
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200)
      ? res.data
      : { code: 'error', message: 'Unexpected response' });
}

function logInitialFill(jobId) {
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/log-initial-fill`,
      {},
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200)
      ? res.data
      : { code: 'error', message: 'Unexpected response' });
}

function logPpeCheck(jobId, beforeIndex) {
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/log-ppe-check/${beforeIndex}`,
      {},
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200)
      ? res.data
      : { code: 'error', message: 'Unexpected response' });
}

function logTask(jobId, taskName) {
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/log-task`,
      { task_name: taskName },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200)
      ? res.data
      : { code: 'error', message: 'Unexpected response' });
}

function refillSpray(jobId, pauseZone = null) {
  // Refill is triggered by the cleanup-page prompt on a refill-planned
  // job. Backend computes the new job's water_quantity (same tractor /
  // same tank) and total_spray_volume (old total minus one tank's
  // worth), so the client only sends the pause-zone location.
  const body = pauseZone ? { pause_zone: pauseZone } : {};
  return axios
    .post(
      `${apiEndpoint.sprayJobs}/${jobId}/refill`,
      body,
      { headers: authHeader() }
    )
    .then((res) => {
      if (res && res.status === 200) {
        return res.data;
      }
      return { code: 'error', message: 'Unexpected response' };
    });
}
